import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  FooterButtons,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from './styles';

import type { TimePreset } from '@controlrooms/components';
import { Button, Icon, TimeframePicker } from '@controlrooms/components';
import { DateFormats, TimeSelection } from '@controlrooms/models';
import { TimeUtils } from '@controlrooms/utils';

const InputContainer = styled.div`
  content-stretch: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  border-radius: 2px;
  flex-shrink: 0;
  width: 100%;
`;

const InputField = styled.div`
  background-color: ${({ theme }) => theme.modelManager.model.sectionGroupBG};
  height: 30px;
  position: relative;
  border-radius: 3px;
  flex-shrink: 0;
  width: 100%;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 8px 4px 8px 8px;
`;

const InputContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1 0 0;
  gap: 2px;
  align-items: center;
  min-height: 1px;
  min-width: 1px;
  padding-bottom: 1px;
  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
  position: relative;
  flex-shrink: 0;
`;

const InputText = styled.div`
  flex: 1 0 0;
  font-family: 'Open Sans', sans-serif;
  font-style: italic;
  font-weight: 400;
  line-height: 0;
  min-height: 1px;
  min-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  flex-shrink: 0;
  font-size: 11px;
  color: ${({ theme }) => theme.modelManager.textSecondary};
  white-space: nowrap;
`;

const InputTextContent = styled.p`
  line-height: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  padding: 5px 10px;
`;

const InputShadow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 1px 2px 4px 0px rgba(0, 0, 0, 0.1);
`;

const InputBorder = styled.div`
  position: absolute;
  border: 0.3px solid ${({ theme }) => theme.modelManager.inputBorder};
  border-style: solid;
  inset: 0;
  pointer-events: none;
  border-radius: 3px;
`;

interface TimeRangePickerWithInputProps {
  // Required controlled component props
  value: string; // UTC ISO string
  onChange: (value: string) => void; // UTC ISO string

  // Display props
  placeholder?: string;
  style?: React.CSSProperties;

  // TimeframePicker props
  mode?: 'single' | 'range';
  calendarLayout?: 'single' | 'dual';
  includeTime?: boolean;
  allowTimezoneChange?: boolean;
  showTimezone?: boolean;
  timeFormat?: '12h' | '24h';
  presets?: TimePreset[];
  timePresetMap?: Map<string, number>;
  plantTimezone?: string;
  onOpenChange?: (isOpen: boolean) => void;
}

export const TimeRangePickerWithInput = ({
  // Required controlled props
  value,
  onChange,
  // Display props
  placeholder = 'Select date',
  style,
  // TimeframePicker props
  mode = 'single',
  calendarLayout = 'single',
  includeTime = true,
  allowTimezoneChange = false,
  showTimezone = true,
  timeFormat = '24h',
  presets = [],
  timePresetMap = new Map(),
  plantTimezone,
  onOpenChange,
}: TimeRangePickerWithInputProps) => {
  const tenantTimezone = plantTimezone || dayjs.tz.guess();
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<TimeSelection | null>(null);

  const timezone = plantTimezone || tenantTimezone;
  const timeFormatPattern = timeFormat === '12h' ? 'hh:mm:ss A' : 'HH:mm:ss';

  // Format the display value for the readonly input
  const displayValue = useMemo(() => {
    if (value) {
      const localDateTime = TimeUtils.fromUtcToTimezone(value, timezone);
      const timezoneAbbr = TimeUtils.zoneFormat(timezone, 'z');
      return `${localDateTime.format(`MMM DD YY, ${timeFormatPattern}`)} ${timezoneAbbr}`;
    }
    return '';
  }, [value, timezone, timeFormatPattern]);

  // Create current time selection from value
  const currentTimeSelection = useMemo(() => {
    if (value) {
      return new TimeSelection(dayjs(value), dayjs(value), timezone, 0, undefined, undefined);
    }
    return new TimeSelection(dayjs(), dayjs(), timezone, 0, undefined, undefined);
  }, [value, timezone]);

  // Handle draft changes
  const handleDraftChange = useCallback((newDraft: TimeSelection | null) => {
    setDraftValue(newDraft);
  }, []);

  // Handle OK button click
  const handleOk = useCallback(() => {
    if (draftValue) {
      const utcDateTime = draftValue.startTime.utc().format(DateFormats.DATETIME_WITHOUT_TIMEZONE);
      onChange(utcDateTime);
    }
    setIsOpen(false);
    setDraftValue(null);
  }, [draftValue, onChange]);

  // Handle modal close functionality
  const handleModalClose = useCallback(() => {
    setIsOpen(false);
    setDraftValue(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  // Handle Cancel button click
  const handleCancel = useCallback(() => {
    handleModalClose();
  }, [handleModalClose]);

  // Handle escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleModalClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleModalClose]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }}>
        <InputContainer>
          <InputField
            onClick={() => {
              setIsOpen(true);
              onOpenChange?.(true);
            }}
          >
            <InputContent>
              <InputText>
                <InputTextContent>{displayValue || placeholder}</InputTextContent>
              </InputText>
            </InputContent>
            <InputShadow />
            <InputBorder aria-hidden="true" />
          </InputField>
        </InputContainer>
      </div>

      {isOpen &&
        createPortal(
          <ModalOverlay onClick={handleModalClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Select Date & Time</ModalTitle>
                <ModalCloseButton onClick={handleModalClose}>
                  <Icon name="close" />
                </ModalCloseButton>
              </ModalHeader>
              <TimeframePicker
                value={draftValue || currentTimeSelection}
                presets={presets}
                timePresetMap={timePresetMap}
                onDraftChange={handleDraftChange}
                onPresetApply={(selection) => {
                  const utcDateTime = selection.startTime
                    .utc()
                    .format(DateFormats.DATETIME_WITHOUT_TIMEZONE);
                  onChange(utcDateTime);
                  setIsOpen(false);
                  setDraftValue(null);
                }}
                mode={mode}
                calendarLayout={calendarLayout}
                includeTime={includeTime}
                allowTimezoneChange={allowTimezoneChange}
                timeFormat={timeFormat}
                plantTimezone={timezone}
                showTimezone={showTimezone}
              />
              <ModalFooter>
                <FooterButtons>
                  <Button buttonType="secondary" buttonSize="small" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button buttonType="primary" buttonSize="small" onClick={handleOk}>
                    OK
                  </Button>
                </FooterButtons>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>,
          document.body,
        )}
    </>
  );
};
